import React from 'react';
import { Shield, ChevronDown, ChevronUp, Mail, FlaskConical, Pill, Activity } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '../ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { LETTER_TYPES, PROFILE_RESTRICTIONS } from '../../constants/mediscribe';
import { ClipboardCheck } from 'lucide-react';
import {
  ImagingOrdersInput, SpecialtyReferralsInput, LetterSelector,
} from './index';
import ProvocativeTestsPicker from './ProvocativeTestsPicker';
import MinimalCollapse from './MinimalCollapse';
import { LabsChipInput } from './LabsChipInput';
import { MedsChipInput } from './MedsChipInput';

/**
 * ProviderToggleSection — The toggle row + expanded sections for provider mode.
 * Extracted from App.js to reduce monolith size.
 */
const ProviderToggleSection = ({
  // Toggle states
  includeROS, setIncludeROS,
  includePE, setIncludePE,
  peUnremarkable, setPeUnremarkable,
  peExceptions, setPeExceptions,
  markPeFindingsManual,
  imagingOrders, showImagingSection, setShowImagingSection,
  addImaging, removeImaging, updateImagingType, updateImagingBodyPart, updateImagingIndication, restoreImaging,
  setImagingOrders,
  specialties, showReferralsSection, setShowReferralsSection,
  addSpecialty, removeSpecialty, updateSpecialty, restoreSpecialty,
  setSpecialties,
  showLabsSection, setShowLabsSection,
  labOrders, setLabOrders,
  medsRxd, setMedsRxd,
  medsRxdDetail, setMedsRxdDetail,
  generateLimitations, setGenerateLimitations,
  generateAdminLetter, setGenerateAdminLetter,
  letterType, setLetterType,
  customLetterDescription, setCustomLetterDescription,
  includeEmAnalysis, setIncludeEmAnalysis,
  // PE options
  charLimit, setCharLimit,
  showAdvanced, setShowAdvanced,
  // Quick actions
  quickActions,
  addQuickActionImaging, addQuickActionReferral,
  // Restrictions
  selectedRestriction, setSelectedRestriction,
  showRestrictionsHelper, setShowRestrictionsHelper,
  copyRestrictions,
  // Letter
  providerLetterTone, setProviderLetterTone,
  includePatientQuotes, setIncludePatientQuotes,
  consultRecipient, setConsultRecipient,
  consultQuestion, setConsultQuestion,
  // Feb 28, 2026 (v6.8.43) — Auto-seed explainer map + dismiss callback.
  autoSeedReasons,
  clearAutoSeedReason,
}) => {
  // Mar 3, 2026 — Auto-flip Unremarkable → Remarkable when the provider
  // types (or chip-taps) ANY finding into the exceptions box. Rationale
  // from provider: "if I'm typing findings, they clearly aren't
  // unremarkable — the note should treat them as real remarkable
  // overrides, not as an 'unremarkable except...' preamble."
  // Only fires on the transition from empty → non-empty; once the
  // provider has flipped Remarkable, further edits (or clearing) do NOT
  // re-toggle — so they can manually flip back to Unremarkable if they
  // decide the findings actually fit the 'unremarkable except' pattern.
  const handleSetPeExceptions = (next) => {
    const nextStr = typeof next === 'function' ? next(peExceptions) : next;
    const prevEmpty = !(peExceptions || '').trim();
    const nextHasText = !!(nextStr || '').trim();
    if (prevEmpty && nextHasText && peUnremarkable && setPeUnremarkable) {
      setPeUnremarkable(false);
    }
    setPeExceptions(nextStr);
  };
  return (
  <>
    {/* Feb 27, 2026 (v2) — Outputs picker MOVED to the top sticky
        encounter header (OutputsHeaderRow). Provider asked for
        always-visible checkboxes across the top instead of a popover.
        No trigger here anymore — the row above the grid handles it. */}

    {/* Expanded sections */}
    <div className="space-y-3">
      {/* PE Options */}
      {includePE && (
        <div className="pl-2 animate-in slide-in-from-top-2 duration-200 py-1.5 bg-secondary/30 rounded-lg px-3" data-testid="pe-options">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-[11px] text-muted-foreground font-medium">PE:</span>
            <div className="flex items-center space-x-1.5">
              <Checkbox id="pe-unremarkable" checked={peUnremarkable} onCheckedChange={setPeUnremarkable} className="h-3.5 w-3.5" data-testid="checkbox-pe-unremarkable" />
              <label htmlFor="pe-unremarkable" className="text-[11px] text-muted-foreground cursor-pointer">Unremarkable</label>
            </div>
            <div className="flex items-center space-x-1.5">
              <Checkbox id="pe-remarkable" checked={!peUnremarkable} onCheckedChange={(checked) => setPeUnremarkable(!checked)} className="h-3.5 w-3.5" data-testid="checkbox-pe-remarkable" />
              <label htmlFor="pe-remarkable" className="text-[11px] text-muted-foreground cursor-pointer">Remarkable</label>
            </div>
            <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced} className="ml-auto">
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] text-muted-foreground hover:text-foreground">
                  Char Limit <ChevronDown className={`w-3 h-3 ml-1 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-1">
                <Input type="number" placeholder="500" value={charLimit} onChange={(e) => setCharLimit(e.target.value)} className="h-7 w-20 text-xs font-mono bg-input border-border" data-testid="char-limit-input" />
              </CollapsibleContent>
            </Collapsible>
          </div>
          {/* PERTINENT PE FINDINGS — full-width prominent textarea on its own row */}
          {setPeExceptions && (
            <div className="mt-2">
              <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
                <label
                  htmlFor="pe-exceptions-textarea"
                  className="block text-[11px] font-bold tracking-wide text-amber-400 uppercase"
                >
                  {peUnremarkable ? 'EXCEPT:' : 'PERTINENT PE FINDINGS:'}
                </label>
                <ProvocativeTestsPicker
                  peExceptions={peExceptions}
                  setPeExceptions={handleSetPeExceptions}
                  markPeFindingsManual={markPeFindingsManual}
                />
              </div>
              {/* Quick-tap chip row — appends common findings with comma separator.
                  Skips chip if its token already appears (case-insensitive substring). */}
              {/* Feb 28, 2026 — slimmer pill style (h-5 + py-0 + rounded-full)
                  per user feedback: previous chunky boxes felt "kindergarten".
                  Mar 1, 2026 — Wrapped in MinimalCollapse so in Minimal Mode
                  the 16-chip cluster collapses behind a "Body Region Chips" disclosure.
                  Normal mode renders inline as before. */}
              <MinimalCollapse
                label="Body Region Chips"
                summary={`${[
                  'TTP', '+SLR', '+McMurray', '+Lachman', '+Spurling', '+Hawkins',
                  '+Tinel', '+Phalen', 'decreased ROM', 'limited flexion', 'crepitus',
                  'edema', 'ecchymosis', 'antalgic gait', 'guarding', '4/5 strength',
                ].filter((t) => (peExceptions || '').toLowerCase().includes(t.toLowerCase())).length}/16`}
                testid="minimal-collapse-body-chips"
              >
              <div className="flex flex-wrap gap-1 mb-1.5" data-testid="pe-findings-chips">
                {[
                  'TTP', '+SLR', '+McMurray', '+Lachman', '+Spurling', '+Hawkins',
                  '+Tinel', '+Phalen', 'decreased ROM', 'limited flexion', 'crepitus',
                  'edema', 'ecchymosis', 'antalgic gait', 'guarding', '4/5 strength',
                ].map((token) => {
                  const present = (peExceptions || '').toLowerCase().includes(token.toLowerCase());
                  return (
                    <button
                      key={token}
                      type="button"
                      onClick={() => {
                        const cur = (peExceptions || '').trim();
                        const next = cur
                          ? (cur.endsWith(',') ? `${cur} ${token}` : `${cur}, ${token}`)
                          : token;
                        handleSetPeExceptions(next);
                        if (markPeFindingsManual) markPeFindingsManual();
                      }}
                      disabled={present}
                      className={`text-[10px] px-2 py-0.5 rounded-full border leading-tight transition-colors ${
                        present
                          ? 'bg-amber-500/20 border-amber-500/40 text-amber-300 cursor-default opacity-60'
                          : 'bg-secondary/40 border-border text-muted-foreground hover:bg-amber-500/15 hover:border-amber-500/40 hover:text-amber-300'
                      }`}
                      title={present ? `${token} already added` : `Add "${token}"`}
                      data-testid={`pe-chip-${token.replace(/\s+/g, '-').replace(/[^a-z0-9-]/gi, '').toLowerCase()}`}
                    >
                      {token}
                    </button>
                  );
                })}
              </div>
              </MinimalCollapse>
              <Textarea
                id="pe-exceptions-textarea"
                placeholder={peUnremarkable
                  ? 'e.g., bilateral knee crepitus, limited ROM lumbar'
                  : 'e.g., TTP L4-L5, +SLR right, decreased ROM flexion 40°, +Spurling, antalgic gait'}
                value={peExceptions}
                onChange={(e) => { handleSetPeExceptions(e.target.value); if (markPeFindingsManual) markPeFindingsManual(); }}
                rows={3}
                className="min-h-[72px] text-xs bg-input border-amber-500/30 focus:border-amber-500/60 focus:ring-amber-500/20 w-full resize-y leading-relaxed"
                data-testid="pe-exceptions-input"
              />
            </div>
          )}
        </div>
      )}

      {/* Mar 2, 2026 — Imaging + Referral sections moved into a single
          floating Dialog so opening them doesn't push PE / notes down.
          Per provider request: "popout at banner instead of shift the
          screen to below PE". Dialog stays open while showImagingSection
          OR showReferralsSection is on; closing sets both to false but
          leaves the chip lists intact (they still surface downstream via
          OutputsHeaderRow's imagingOn / referralsOn indicators). */}
      <Dialog
        open={showImagingSection || showReferralsSection}
        onOpenChange={(open) => {
          if (!open) {
            setShowImagingSection(false);
            setShowReferralsSection(false);
          }
        }}
      >
        <DialogContent
          className="max-w-2xl bg-card border-primary/30"
          data-testid="imaging-referral-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-base font-semibold">
              {showImagingSection && showReferralsSection ? 'Imaging Orders & Referrals'
                : showImagingSection ? 'Imaging Orders' : 'Specialty Referrals'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-1 max-h-[70vh] overflow-y-auto">
            {(imagingOrders.length > 0 || showImagingSection) && (
              <div data-testid="imaging-section-wrapper">
                <ImagingOrdersInput
                  imagingOrders={imagingOrders}
                  addImaging={addImaging}
                  removeImaging={removeImaging}
                  updateImagingType={updateImagingType}
                  updateImagingBodyPart={updateImagingBodyPart}
                  updateImagingIndication={updateImagingIndication}
                  restoreImaging={restoreImaging}
                  quickActions={quickActions}
                  addQuickActionImaging={addQuickActionImaging}
                  autoSeedReasons={autoSeedReasons}
                  clearAutoSeedReason={clearAutoSeedReason}
                />
              </div>
            )}

            {(specialties.length > 0 || showReferralsSection) && (
              <div data-testid="referral-section-wrapper">
                <SpecialtyReferralsInput
                  specialties={specialties}
                  addSpecialty={addSpecialty}
                  removeSpecialty={removeSpecialty}
                  restoreSpecialty={restoreSpecialty}
                  updateSpecialty={(idx, val) => {
                    updateSpecialty(idx, val);
                    // Auto-open imaging section when Radiology/Diagnostic Imaging is selected
                    if (val === 'Radiology/Diagnostic Imaging' && !showImagingSection) {
                      setShowImagingSection(true);
                      if (imagingOrders.length === 0) addImaging();
                    }
                  }}
                  quickActions={quickActions}
                  addQuickActionReferral={addQuickActionReferral}
                  autoSeedReasons={autoSeedReasons}
                  clearAutoSeedReason={clearAutoSeedReason}
                />
              </div>
            )}

            <div className="flex justify-end pt-2 border-t border-border">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => { setShowImagingSection(false); setShowReferralsSection(false); }}
                data-testid="imaging-referral-dialog-done"
              >
                Done
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Labs Ordered — Feb 28, 2026 sleek chip-row replaces the
          bulky textarea so providers can tap-pick common labs (CBC,
          CMP, A1C, TSH…) in a single popover. Round-trips with the
          existing `labOrders` comma-separated string so backend prompt
          builders need no changes. */}
      {showLabsSection && (
        <div className="animate-in slide-in-from-top-2 duration-200 py-2 border-t border-border">
          <LabsChipInput labOrders={labOrders} setLabOrders={setLabOrders} />
        </div>
      )}

      {/* Meds Rx'd — Feb 28, 2026 structured chip-row. The boolean
          `medsRxd` still drives the LLM prompt's "yes meds prescribed"
          branch; the `medsRxdDetail` string (built from chips) is
          surfaced alongside so the model receives the structured Rx
          list when present, otherwise falls back to inferring from
          the dictation. */}
      {medsRxd && setMedsRxdDetail && (
        <div className="animate-in slide-in-from-top-2 duration-200 py-2 border-t border-border">
          <MedsChipInput
            medsRxdDetail={medsRxdDetail}
            setMedsRxdDetail={setMedsRxdDetail}
            onAddTriggersBoolean={() => { if (!medsRxd) setMedsRxd(true); }}
          />
        </div>
      )}

      {/* Profile/Restrictions */}
      {generateLimitations && (
        <div className="animate-in slide-in-from-top-2 duration-200">
          <Collapsible open={showRestrictionsHelper} onOpenChange={setShowRestrictionsHelper}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="w-full justify-between text-muted-foreground hover:text-foreground h-7 text-xs">
                <span className="flex items-center gap-1.5">
                  <Shield className="w-3 h-3 text-green-500" />
                  Restrictions Helper
                  <Badge variant="outline" className="text-[9px] bg-green-500/20 text-green-400 border-green-500/30 px-1">MEDPROS</Badge>
                </span>
                {showRestrictionsHelper ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-2 pt-2">
              <p className="text-xs text-muted-foreground">HIPAA-safe restriction language for MEDPROS free text fields.</p>
              <Select value={selectedRestriction} onValueChange={setSelectedRestriction}>
                <SelectTrigger className="h-8 text-xs bg-input" data-testid="restriction-select"><SelectValue placeholder="Select condition type..." /></SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Neurological/BH</SelectLabel>
                    {['concussion_mTBI', 'migraines', 'behavioral_health', 'sleep_disorder', 'medication_adjustment', 'trauma_recovery'].map(v => {
                      const r = PROFILE_RESTRICTIONS.find(x => x.value === v);
                      return r ? <SelectItem key={v} value={v}>{r.label}</SelectItem> : null;
                    })}
                  </SelectGroup>
                  <SelectGroup>
                    <SelectLabel>Musculoskeletal</SelectLabel>
                    {['upper_extremity', 'lower_extremity', 'back_limitation'].map(v => {
                      const r = PROFILE_RESTRICTIONS.find(x => x.value === v);
                      return r ? <SelectItem key={v} value={v}>{r.label}</SelectItem> : null;
                    })}
                  </SelectGroup>
                  <SelectGroup>
                    <SelectLabel>Medical/Surgical</SelectLabel>
                    {['post_surgical', 'respiratory', 'cardiac'].map(v => {
                      const r = PROFILE_RESTRICTIONS.find(x => x.value === v);
                      return r ? <SelectItem key={v} value={v}>{r.label}</SelectItem> : null;
                    })}
                  </SelectGroup>
                  <SelectGroup>
                    <SelectLabel>Pregnancy/Postpartum</SelectLabel>
                    {['pregnancy', 'postpartum'].map(v => {
                      const r = PROFILE_RESTRICTIONS.find(x => x.value === v);
                      return r ? <SelectItem key={v} value={v}>{r.label}</SelectItem> : null;
                    })}
                  </SelectGroup>
                </SelectContent>
              </Select>
              {selectedRestriction && (() => {
                const restriction = PROFILE_RESTRICTIONS.find(r => r.value === selectedRestriction);
                if (!restriction) return null;
                return (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-primary">{restriction.label}</span>
                      <Badge variant="outline" className="text-xs">{restriction.duration}</Badge>
                    </div>
                    <div className="bg-secondary/50 rounded-lg p-2 space-y-1 max-h-[120px] overflow-y-auto custom-scrollbar">
                      {restriction.restrictions.map((r, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-xs"><span className="text-primary">•</span><span className="text-foreground">{r}</span></div>
                      ))}
                    </div>
                    <Button variant="default" size="sm" className="w-full h-7 bg-primary hover:bg-primary/90 text-xs" onClick={() => copyRestrictions(restriction.restrictions)} data-testid="copy-restrictions-btn">
                      <ClipboardCheck className="w-3 h-3 mr-1.5" />Copy for MEDPROS
                    </Button>
                  </div>
                );
              })()}
            </CollapsibleContent>
          </Collapsible>
        </div>
      )}

      {/* Letter Options */}
      {(generateAdminLetter || !!letterType) && (
        <div id="letter-section" className="animate-in slide-in-from-top-2 duration-200">
          <Collapsible defaultOpen={generateAdminLetter || !!letterType}>
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className={`w-full justify-between h-9 font-medium transition-all ${
                  letterType
                    ? 'text-primary bg-primary/10 border border-primary/30'
                    : 'text-foreground hover:text-primary border border-border hover:border-primary/30'
                }`}
              >
                <span className="flex items-center gap-2 text-xs">
                  <Mail className="w-3.5 h-3.5" />
                  {letterType ? `Letter: ${LETTER_TYPES.find(l => l.value === letterType)?.label || letterType}` : 'Select Letter Type'}
                </span>
                <ChevronDown className="w-4 h-4" />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-3">
              <LetterSelector
                letterType={letterType}
                setLetterType={setLetterType}
                setGenerateAdminLetter={setGenerateAdminLetter}
                customLetterDescription={customLetterDescription}
                setCustomLetterDescription={setCustomLetterDescription}
                letterTone={providerLetterTone}
                setLetterTone={setProviderLetterTone}
                includePatientQuotes={includePatientQuotes}
                setIncludePatientQuotes={setIncludePatientQuotes}
                consultRecipient={consultRecipient}
                setConsultRecipient={setConsultRecipient}
                consultQuestion={consultQuestion}
                setConsultQuestion={setConsultQuestion}
              />
            </CollapsibleContent>
          </Collapsible>
        </div>
      )}
    </div>
  </>
  );
};

export default ProviderToggleSection;
